import http from "../utils/http";
import { GETListResponse, ISubscription } from "./SubscriptionServices";

interface GetMemberParams {
  page: number;
  size: number;
  role: string;
}

// Initialize skipAuthRefresh
const skipAuthRefresh = true;

const getMember = async ({ page, size, role }: GetMemberParams) => {
  let urlPath = `/admin/user/customers?page=${page - 1}&size=${size}`;

  if (role !== "ALL") {
    urlPath += `&memberType=${role}`;
  }

  const response = await http.get({ path: urlPath });

  if (response.status === 200) {
    return response.data;
  }
};

const createMember = async (memberData: any) => {
  const urlPath = `/admin/user/`;
  const createdMember = await http.post({
    path: urlPath,
    body: memberData,
  });
  return createdMember;
};
const UpdateMember = async (memberData: MemberResponse) => {
  const urlPath = `/admin/user`;
  const result = await http.put({
    path: urlPath,
    body: memberData,
  });

  if (result.status == 200) {
    return 200;
  } else {
    return 401;
  }
};

export interface MemberResponse {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dob?: string;
  memberType: "VISITOR" | "SUBSCRIBER" | "GYM_MEMBER" | "ONLINE_MEMBER";
  healthData: {
    age?: number;
    height: number;
    weight: number;
  };
  gender: string;
  createdAt: string;
  updatedAt: string;
  photoUrl: string;
  registered: boolean;
  active: boolean;
  admin: boolean;
}

const GetOneMember = async ({
  id,
}: {
  id: string;
}): Promise<MemberResponse> => {
  const urlpath = `/admin/user/${id}`;
  const result = await http.get({ path: urlpath });
  return result.data;
};

const GetMemberProfilePictureUploadUrl = async ({
  userId,
  fileName,
}: {
  userId: string;
  fileName: string;
}) => {
  const urlPath = `/admin/user/upload-avatar?userId=${userId}&fileName=${encodeURIComponent(
    fileName
  )}`;
  return http.get({ path: urlPath });
};

const UpdateMemberProfilePicture = async ({
  userId,
  photoUrl,
}: {
  userId: string;
  photoUrl: string;
}): Promise<number> => {
  const urlPath = `/admin/user/update-avatar?userId=${userId}&photoUrl=${encodeURIComponent(
    photoUrl
  )}`;
  const result = await http.put({ path: urlPath, body: {} });

  if (result.status === 200) {
    return 200;
  } else {
    return 401;
  }
};

const DeleteMemberProfilePicture = async ({
  userId,
}: {
  userId: string;
}): Promise<number> => {
  const urlPath = `/admin/user/remove-avatar?userId=${userId}`;
  const result = await http.deleteHttp({ path: urlPath, body: {} });

  if (result.status === 204) {
    return 204;
  } else {
    return 400;
  }
};

export interface MemberSubscriptionsData {
  id: string;
  createdAt: string;
  endDate: string;
  startDate: string;
  status: string;
  activePauseId?: string | null;
  totalPausedDays?: number;
  subscription: ISubscription;
  user: MemberResponse;
}

export type PauseReason =
  | "TRAVEL"
  | "MEDICAL"
  | "PERSONAL"
  | "FINANCIAL"
  | "OTHER";

export interface PauseSubscriptionResponse {
  pauseId: string;
  userSubscriptionId: string;
  status: string;
  pausedAt: string;
  effectivePauseEnd: string;
  remainingBudgetAtPause: number;
  endDate: string;
  totalPausedDays: number;
}

export interface ResumeSubscriptionResponse {
  pauseId: string;
  userSubscriptionId: string;
  status: string;
  resumedAt: string;
  resumeType: string;
  actualPausedDays: number;
  remainingAtPause: number;
  previousEndDate: string;
  newEndDate: string;
  totalPausedDays: number;
}

export interface SubscriptionPauseHistoryItem {
  id: string;
  userSubscriptionId: string;
  pausedAt: string;
  reason: PauseReason;
  adminNote?: string;
  endDateAtPauseStart: string;
  effectivePauseEnd: string;
  status: "ACTIVE" | "COMPLETED";
  resumedAt?: string;
  resumeType?: "MANUAL" | "AUTO";
  resumeNote?: string;
  actualPausedDays?: number;
}
const MemberSubscriptions = async ({
  id,
}: {
  id: string;
}): Promise<MemberSubscriptionsData[]> => {
  const urlpath = `/admin/user-subscriptions/user/${id}?page=0&size=10`;
  const result = await http.get({ path: urlpath });
  return result.data.content;
};

const ChangeMemberStatus = async ({
  userId,
  isActive,
}: {
  userId: string;
  isActive: boolean;
}): Promise<number> => {
  const urlPath = `/admin/user/status`;
  const result = await http.put({ path: urlPath, body: { isActive, userId } });

  if (result.status == 200) {
    return 200;
  } else {
    return 401;
  }
};

interface ISavedMemberNote {
  id: string;
  createdAt: string;
  note: string;
  updatedAt: string;
  userId: string;
}
const SaveMemberNote = async ({
  userId,
  note,
}: {
  userId: string;
  note: string;
}): Promise<ISavedMemberNote> => {
  const urlPath = `/admin/notes`;
  const result = await http.post({ path: urlPath, body: { userId, note } });

  console.log("notes result", result);
  if (result.status == 201) {
    return result.data;
  } else {
    throw new Error("Failed to add note");
  }
};

interface IMemberAttendance {
  id: string;
  userId: string;
  date: string; // Date in ISO 8601 format
  status?: "PRESENT" | "ABSENT";
}

const MemberAttendence = async ({
  memberId,
  year,
  month,
}: {
  memberId: string;
  year: number;
  month: number;
}): Promise<IMemberAttendance[]> => {
  const urlPath = `/admin/attendance/${memberId}?year=${year}&month=${
    month + 1
  }`;
  const result = await http.get({ path: urlPath });
  if (result.status == 200) {
    return result.data as IMemberAttendance[];
  } else {
    throw new Error("Failed to Get Attendence");
  }
};

const MarkMemberAttendance = async ({
  memberId,
  date,
  status,
}: {
  memberId: string;
  date: string;
  status?: "PRESENT" | "ABSENT";
}): Promise<IMemberAttendance> => {
  const urlPath = "/admin/attendance";
  const result = await http.post({
    path: urlPath,
    body: { userId: memberId, date, status },
  });

  if (result.status === 201) {
    return result.data.data as IMemberAttendance;
  } else {
    throw new Error("Failed to mark attendance");
  }
};

const UnmarkMemberAttendance = async ({
  memberId,
  date,
}: {
  memberId: string;
  date: string;
}): Promise<number> => {
  const urlPath = `/admin/attendance/${memberId}?date=${date}`;
  const result = await http.deleteHttp({ path: urlPath, body: {} });

  if (result.status === 204) {
    return 204;
  } else {
    throw new Error("Failed to unmark attendance");
  }
};

const MarkMemberAbsent = async ({
  memberId,
  date,
}: {
  memberId: string;
  date: string;
}): Promise<IMemberAttendance> => {
  return MarkMemberAttendance({ memberId, date, status: "ABSENT" });
};

interface IMemberNotes {
  id: string;
  userId: string;
  note: string;
  createdAt: string;
  updatedAt: string;
}

const GetMemberNotes = async ({
  memberId,
  pageSize,
  page,
}: {
  memberId: string;
  pageSize: number;
  page: number;
}): Promise<GETListResponse<IMemberNotes>> => {
  const urlPath = `/admin/notes/${memberId}?size=${pageSize}&page=${page - 1}`;
  const result = await http.get({ path: urlPath });
  if (result.status == 200) {
    return result.data as GETListResponse<IMemberNotes>;
  } else {
    throw new Error("Failed to Get MemberNotes");
  }
};

const DeleteOneNote = async ({
  noteId,
}: {
  noteId: string;
}): Promise<number> => {
  const urlPath = `/admin/notes/${noteId}`;
  const result = await http.deleteHttp({ path: urlPath, body: {} });
  if (result.status == 204) {
    return 204;
  } else {
    return 400;
  }
};

const AssignSubscription = async ({
  memberID,
  subscriptionId,
}: {
  memberID: string;
  subscriptionId: string;
}): Promise<number> => {
  const urlPath = "/admin/user-subscriptions";

  const result = await http.post({
    path: urlPath,
    body: { userId: memberID, subscriptionId },
  });

  if (result.status == 200) {
    return 200;
  } else {
    return 401;
  }
};

export interface CancelSubscriptionRequest {
  revokeAccess: boolean;
  reason?: string;
}

const CancelSubscription = async ({
  userSubscriptionId,
  revokeAccess,
  reason,
}: {
  userSubscriptionId: string;
  revokeAccess: boolean;
  reason?: string;
}): Promise<MemberSubscriptionsData> => {
  const urlPath = `/admin/user-subscriptions/${userSubscriptionId}/cancel`;

  const result = await http.put({
    path: urlPath,
    body: { revokeAccess, reason },
  });

  if (result.status === 200) {
    return result.data as MemberSubscriptionsData;
  } else {
    throw new Error("Failed to cancel subscription");
  }
};

const PauseSubscription = async ({
  userSubscriptionId,
  reason,
  adminNote,
}: {
  userSubscriptionId: string;
  reason: PauseReason;
  adminNote?: string;
}): Promise<PauseSubscriptionResponse> => {
  const urlPath = `/admin/user-subscriptions/${userSubscriptionId}/pause`;

  const result = await http.post({
    path: urlPath,
    body: { reason, adminNote },
  });

  if (result.status === 200) {
    return result.data as PauseSubscriptionResponse;
  } else {
    throw new Error("Failed to pause subscription");
  }
};

const ResumeSubscription = async ({
  userSubscriptionId,
  resumeNote,
}: {
  userSubscriptionId: string;
  resumeNote?: string;
}): Promise<ResumeSubscriptionResponse> => {
  const urlPath = `/admin/user-subscriptions/${userSubscriptionId}/resume`;

  const result = await http.post({
    path: urlPath,
    body: { resumeNote },
  });

  if (result.status === 200) {
    return result.data as ResumeSubscriptionResponse;
  } else {
    throw new Error("Failed to resume subscription");
  }
};

const StartSubscription = async ({
  userSubscriptionId,
}: {
  userSubscriptionId: string;
}): Promise<MemberSubscriptionsData> => {
  const urlPath = `/admin/user-subscriptions/${userSubscriptionId}/start`;

  const result = await http.post({
    path: urlPath,
    body: {},
  });

  if (result.status === 200) {
    return result.data as MemberSubscriptionsData;
  } else {
    throw new Error("Failed to start subscription");
  }
};

const GetSubscriptionPauseHistory = async ({
  userSubscriptionId,
  page = 0,
  size = 10,
}: {
  userSubscriptionId: string;
  page?: number;
  size?: number;
}): Promise<GETListResponse<SubscriptionPauseHistoryItem>> => {
  const urlPath = `/admin/user-subscriptions/${userSubscriptionId}/pauses?page=${page}&size=${size}`;
  const result = await http.get({ path: urlPath });

  if (result.status === 200) {
    return result.data as GETListResponse<SubscriptionPauseHistoryItem>;
  } else {
    throw new Error("Failed to fetch pause history");
  }
};

export {
  getMember,
  createMember,
  GetOneMember,
  GetMemberProfilePictureUploadUrl,
  UpdateMemberProfilePicture,
  DeleteMemberProfilePicture,
  UpdateMember,
  MemberSubscriptions,
  ChangeMemberStatus,
  SaveMemberNote,
  MemberAttendence,
  MarkMemberAttendance,
  MarkMemberAbsent,
  UnmarkMemberAttendance,
  GetMemberNotes,
  DeleteOneNote,
  AssignSubscription,
  CancelSubscription,
  PauseSubscription,
  ResumeSubscription,
  StartSubscription,
  GetSubscriptionPauseHistory,
};
